import { action } from '@ember/object';
import type Owner from '@ember/owner';
import { next } from '@ember/runloop';
import { service } from '@ember/service';
import Component from '@glimmer/component';
import { cached, tracked } from '@glimmer/tracking';

import type { Service } from 'webapp-types';
import { TemplateObjectType } from 'webapp/enums/enums';
import { copyObject } from 'webapp/utils/common';

import didUpdate from 'webapp/modifiers/did-update';
import FilterLineProxyComponent from 'webapp/components/filter/line-proxy';
import FilterNoResultsComponent from 'webapp/components/filter/no-results';
import type { SectionBase } from 'webapp/components/metric-library/base';
import type { Args as GridBaseArgs } from 'webapp/components/metric-library/grid';
import MetricLibraryGridTemplatesComponent from 'webapp/components/metric-library/grid/templates';

import FilterSortMetricLibrarySourceTemplates from 'webapp/models-custom/filter-sort/metric-library-source-templates';
import type SourceType from 'webapp/models-custom/source-type';
import type Template from 'webapp/models-custom/template';

export const enum Section {
	BOARD_TEMPLATES = 'board-templates',
	REPORT_TEMPLATES = 'report-templates',
}

const sectionOptions: SectionBase[] = [
	{
		id: Section.BOARD_TEMPLATES,
		title: 'Dashboard templates',
		initialRowCount: 3,
	},
	{
		id: Section.REPORT_TEMPLATES,
		title: 'Report templates',
		initialRowCount: 2,
	},
];

type SectionOption = GridBaseArgs & {
	filterSort: FilterSortMetricLibrarySourceTemplates;
};

interface Args {
	sourceType: SourceType;
}

export default class MetricLibrarySourceTabsTemplatesComponent extends Component<Args> {
	@service('model-manager/templates') declare mmTemplatesService: Service.ModelManager.Templates;
	@service('model-manager/reports') declare mmReportsService: Service.ModelManager.Reports;

	@tracked sections: SectionOption[] = [];

	mainFilterSort: FilterSortMetricLibrarySourceTemplates | null = null;
	allFilterSorts: {
		filterSort: FilterSortMetricLibrarySourceTemplates;
		rootElementSelector: string;
		preventBeforeFilter?: boolean;
	}[] = [];

	constructor(owner: Owner, args: Args) {
		super(owner, args);

		const filterSort = {
			[Section.BOARD_TEMPLATES]: new FilterSortMetricLibrarySourceTemplates(
				{
					getAllItems: () => {
						return this.publicBoardTemplates;
					},
					beforeFilter: () => {
						const newFiltersSelectedValues = filterSort[
							Section.BOARD_TEMPLATES
						].filters.map((filter) => filter.selected);

						this.allFilterSorts.forEach((otherFilterSort) => {
							if (!otherFilterSort.preventBeforeFilter) {
								otherFilterSort.filterSort.filters.forEach((filter, index) => {
									// To prevent "You attempted to update `_selected` on `Filter`, but it had already been used previously in the same computation."
									next(() => {
										const updateSiblingFilter =
											!filter.isSort ||
											!filterSort[Section.BOARD_TEMPLATES]
												.isSortBySearchPointsArray;

										if (updateSiblingFilter) {
											filter.selectOption(newFiltersSelectedValues[index]!);
										}
									});
								});

								otherFilterSort.filterSort.sortProp =
									filterSort[Section.BOARD_TEMPLATES].sortProp;
								otherFilterSort.filterSort.typedFilter =
									filterSort[Section.BOARD_TEMPLATES].typedFilter;
							}
						});
					},
				},
				this.filterPlaceholder,
			),
			[Section.REPORT_TEMPLATES]: new FilterSortMetricLibrarySourceTemplates({
				getAllItems: () => {
					return this.publicReportTemplates;
				},
			}),
		};

		sectionOptions.forEach((option, index) => {
			const isMainFilterSort = index === 0,
				optionFilterSort = filterSort[option.id as Section];

			this.allFilterSorts.push({
				filterSort: optionFilterSort,
				rootElementSelector: `.section.${option.id} ul`,
				preventBeforeFilter: isMainFilterSort,
			});

			if (isMainFilterSort) {
				this.mainFilterSort = optionFilterSort;
			}

			const section: SectionOption = {
				filterSort: optionFilterSort,
				classNames: option.id + ' lazy-grid',
				...copyObject(option),
			};

			this.sections.push(section);
		});
	}

	get filterPlaceholder() {
		return `Search ${this.args.sourceType.name} templates...`;
	}

	get isSearchInputEmpty(): boolean {
		return !this.mainFilterSort?.typedFilter;
	}

	get noSearchResults(): boolean {
		return (
			!this.isSearchInputEmpty &&
			this.allFilterSorts.every((filter) => filter.filterSort.noResults)
		);
	}

	@cached
	get publicTemplates() {
		return this.mmTemplatesService.publicTemplateModels.filter((template) =>
			template.specificSourceTypesKeys.includes(this.args.sourceType.key),
		);
	}

	@cached
	get publicBoardTemplates(): Template[] {
		return this.publicTemplates.filter(
			(template) => template.objectType === TemplateObjectType.BOARD,
		);
	}

	@cached
	get publicReportTemplates(): Template[] {
		return this.publicTemplates.filter(
			(template) => template.objectType === TemplateObjectType.REPORT,
		);
	}

	@action
	onSourceTypeChanged() {
		if (this.mainFilterSort) {
			this.mainFilterSort.searchPlaceholder = this.filterPlaceholder;
		}
	}
	<template>
		<div
			class="metric-library-source-templates"
			{{didUpdate this.onSourceTypeChanged @sourceType}}
		>

			{{#if this.mainFilterSort}}
				<FilterLineProxyComponent @model={{this.mainFilterSort}} />

				{{#if this.noSearchResults}}
					<FilterNoResultsComponent @model={{this.mainFilterSort}} />
				{{/if}}
			{{/if}}

			<div class="ml-sections">
				{{#each this.sections as |section|}}
					{{#if section.filterSort.results.length}}
						<MetricLibraryGridTemplatesComponent
							@filterSort={{section.filterSort}}
							@title={{section.title}}
							@initialRowCount={{section.initialRowCount}}
							@classNames={{section.classNames}}
						/>
					{{/if}}
				{{/each}}
			</div>
		</div>
	</template>
}
